import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQueryClient } from 'react-query';

import { Box, Button, Divider, useTheme } from '@mui/material';

import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { ImageTagResponseDto } from '@bandido/shared/dtos';
import { QueryKeys } from '@frontend/features/common/types';

interface FormProps {
  onClose: () => void;
  data?: ImageTagResponseDto;
}

interface RequestForm {
  name: string;
  color: string;
}

const resolver = yupResolver(
  yup.object().shape({
    name: yup.string().required(),
    color: yup
      .string()
      .test((value) => value !== 'no-selection')
      .required(),
  }),
);

export const Form = ({ onClose, data }: FormProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();

  const methods = useForm({
    resolver,
  });
  const { control, setValue, handleSubmit } = methods;

  const onSubmit = async (formData: RequestForm) => {
    try {
      await client.put(`/hotel/image-tag/${data?.id}`, formData);
      addToast({
        message: t('hotels.hotelImageTag.edit.createdSuccess'),
        color: 'success',
      });
      queryClient.invalidateQueries(QueryKeys.getAllHotelImageTags);
    } catch (e) {
      addToast({
        message: t('hotels.hotelImageTag.edit.createdError'),
        color: 'error',
      });
      console.log(e);
    } finally {
      onClose();
    }
  };

  useEffect(() => {
    if (data) {
      setValue('name', data.name || '', { shouldDirty: true });
      setValue('color', data.color || '', { shouldDirty: true });
    }
  }, [data, setValue]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gap={2}
          sx={{
            maxHeight: 'calc(100vh - 300px)',
            overflowX: 'auto',
            paddingTop: theme.spacing(2),
          }}
        >
          <Box gridColumn="span 12">
            <ControlledTextField
              control={control}
              size="small"
              name="name"
              label={t('hotels.hotelImageTag.create.name')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 10">
            <ControlledTextField
              control={control}
              size="small"
              name="color"
              label={t('hotels.hotelImageTag.create.color')}
              placeholder={t('common.form.onlyLetters')}
              required
              errorMessage={t('common.form.required')}
              fullWidth
            />
          </Box>
          <Box gridColumn="span 2">
            <ControlledTextField control={control} size="small" name="color" type="color" fullWidth />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" gap={theme.spacing(3)} paddingTop={theme.spacing(3)}>
          <Divider />
          <Box
            display="flex"
            justifyContent="space-evenly"
            width="100%"
            gap={theme.spacing(1)}
            sx={{
              '& > button': {
                width: 'calc(50% - 4px)',
              },
            }}
          >
            <Button variant="outlined" color="error" size="medium" onClick={onClose}>
              {t('common.form.discard')}
            </Button>
            <Button variant="contained" color="primary" type="submit" size="medium">
              {t('common.form.save')}
            </Button>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};
